import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { certificatesApi } from "../services/api";
import Logo from "../components/Logo";

const T = { night:"#0A1F3D", royal:"#1263B0", cyan:"#1CBCD3",
  white:"#fff", bg:"#F7F9FC", border:"#E2E8F0", green:"#16A34A", red:"#EF4444" };

export default function VerifyCertificate() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [ref, setRef] = useState(searchParams.get("ref") || "");
  const [hash, setHash] = useState(searchParams.get("hash") || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleVerify = useCallback(async (r, h) => {
    setLoading(true);
    try {
      const res = await certificatesApi.verify(r, h);
      setResult(res.data?.data || { exists: false, valid: false });
    } catch {
      setResult({ exists: false, valid: false });
    } finally { setLoading(false); }
  }, []);

  // Un QR code scanne pointe vers /verifier?ref=...&hash=... : on lance
  // automatiquement la verification une seule fois, au chargement de la page.
  useEffect(() => {
    const qRef = searchParams.get("ref");
    const qHash = searchParams.get("hash");
    if (qRef && qHash) handleVerify(qRef, qHash);
  }, []);

  const checks = result?.exists ? [
    { label: t("verify_cert.check_hash"), ok: result.hashMatches },
    { label: t("verify_cert.check_integrity"), ok: result.dataIntegrity },
    { label: t("verify_cert.check_signature"), ok: result.signatureValid },
  ] : [];

  return (
    <div style={{ minHeight:"100vh", background:T.bg, fontFamily:"'DM Sans',sans-serif",
      display:"flex", alignItems:"center", justifyContent:"center", padding:"24px" }}>
      <div style={{ background:T.white, borderRadius:"20px", padding:"40px",
        maxWidth:"480px", width:"100%", border:"1px solid #E2E8F0" }}>
        <div style={{ textAlign:"center", marginBottom:"32px" }}>
          <Logo size="md" />
          <h1 style={{ fontSize:"20px", fontWeight:700, color:T.night, marginTop:"16px" }}>
            {t("verify_cert.title")}
          </h1>
          <p style={{ color:"#64748B", fontSize:"14px" }}>{t("verify_cert.subtitle")}</p>
        </div>

        {[{label:t("verify_cert.reference"),val:ref,set:setRef,ph:t("verify_cert.reference_placeholder")},
          {label:t("verify_cert.hash"),val:hash,set:setHash,ph:t("verify_cert.hash_placeholder")}
        ].map(f => (
          <div key={f.label} style={{ marginBottom:"16px" }}>
            <label style={{ fontSize:"13px", fontWeight:600, color:"#334155",
              marginBottom:"6px", display:"block" }}>{f.label}</label>
            <input value={f.val} onChange={e=>f.set(e.target.value)}
              placeholder={f.ph}
              style={{ width:"100%", padding:"12px 16px", border:"1.5px solid #E2E8F0",
                borderRadius:"12px", fontSize:"14px", outline:"none", boxSizing:"border-box" }}/>
          </div>
        ))}

        <button onClick={() => handleVerify(ref, hash)} disabled={!ref||!hash||loading}
          style={{ width:"100%", padding:"14px",
            background:ref&&hash&&!loading?T.royal:"#E2E8F0",
            color:ref&&hash&&!loading?T.white:"#94A3B8",
            border:"none", borderRadius:"12px", fontWeight:700,
            cursor:ref&&hash&&!loading?"pointer":"not-allowed", fontSize:"15px",
            marginBottom:"20px" }}>
          {loading?t("common.loading"):t("verify_cert.submit")}
        </button>

        {result && (
          <div style={{ borderRadius:"14px", padding:"20px", textAlign:"center",
            background:result.valid?"#F0FDF4":"#FEF2F2",
            border:`2px solid ${result.valid?T.green:T.red}` }}>
            <div style={{ fontSize:"36px", marginBottom:"8px" }}>
              {result.valid?"✅":"❌"}
            </div>
            <div style={{ fontWeight:700, fontSize:"16px",
              color:result.valid?T.green:"#B91C1C" }}>
              {result.valid?t("verify_cert.valid_title"):
                result.exists?t("verify_cert.compromised_title"):t("verify_cert.invalid_title")}
            </div>
            {!result.exists && (
              <p style={{ color:"#64748B", fontSize:"13px", marginTop:"8px" }}>
                {t("verify_cert.invalid_desc")}
              </p>
            )}

            {result.exists && (
              <div style={{ marginTop:"16px", textAlign:"left", display:"flex",
                flexDirection:"column", gap:"8px" }}>
                {checks.map(c => (
                  <div key={c.label} style={{ display:"flex", alignItems:"center",
                    gap:"8px", fontSize:"13px", color:"#334155" }}>
                    <span style={{ color:c.ok?T.green:T.red, fontWeight:700 }}>
                      {c.ok?"✓":"✕"}
                    </span>
                    {c.label}
                  </div>
                ))}
                {result.dataHash && (
                  <div style={{ marginTop:"6px", fontSize:"10.5px", color:"#94A3B8",
                    wordBreak:"break-all", fontFamily:"monospace" }}>
                    {result.dataHash}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
